import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import RadioCard from './components/RadioCard';
import FilterBar from './components/FilterBar';
import { SwalAlert } from '@/hooks/useSweetAlert';
import {
  globalRadioStations,
  sortRadios,
  type SortOption,
  type GlobalRadioStation,
} from '@/mocks/globalRadios';

export default function RadiosPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState('');
  const [isLive, setIsLive] = useState<boolean | null>(null);
  const [radioScoreMin, setRadioScoreMin] = useState(0);
  const [sort, setSort] = useState<SortOption>('trending');
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('grid');

  const allCategories = useMemo(() => {
    const set = new Set(globalRadioStations.map((r) => r.category));
    return Array.from(set).sort();
  }, []);

  const allCities = useMemo(() => {
    const set = new Set(globalRadioStations.map((r) => r.city));
    return Array.from(set).sort();
  }, []);

  const allCountries = useMemo(() => {
    const set = new Set(globalRadioStations.map((r) => r.country));
    return Array.from(set).sort();
  }, []);

  const allLanguages = useMemo(() => {
    const set = new Set(globalRadioStations.map((r) => r.language));
    return Array.from(set).sort();
  }, []);

  const filtered: GlobalRadioStation[] = useMemo(() => {
    let result = [...globalRadioStations];

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.city.toLowerCase().includes(q) ||
          r.state.toLowerCase().includes(q) ||
          r.frequency.includes(q) ||
          r.description.toLowerCase().includes(q)
      );
    }
    if (category) result = result.filter((r) => r.category === category);
    if (city) result = result.filter((r) => r.city === city);
    if (country) result = result.filter((r) => r.country === country);
    if (language) result = result.filter((r) => r.language === language);
    if (isLive !== null) result = result.filter((r) => r.isLive === isLive);
    if (radioScoreMin > 0) result = result.filter((r) => r.radioScore >= radioScoreMin);

    return sortRadios(result, sort);
  }, [search, category, city, country, language, isLive, radioScoreMin, sort]);

  const liveCount = globalRadioStations.filter((r) => r.isLive).length;
  const totalListeners = useMemo(() => globalRadioStations.reduce((sum, r) => sum + r.listeners, 0), []);

  const clearAllFilters = () => {
    setSearch('');
    setCategory('');
    setCity('');
    setCountry('');
    setLanguage('');
    setIsLive(null);
    setRadioScoreMin(0);
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20broadcast%20radio%20studio%20panoramic%20view%2C%20mixing%20console%20with%20glowing%20colorful%20LED%20buttons%20and%20faders%2C%20multiple%20studio%20monitors%20displaying%20audio%20waveforms%2C%20professional%20microphone%20in%20foreground%2C%20warm%20ambient%20studio%20lighting%2C%20acoustic%20panels%20on%20walls%2C%20high-end%20professional%20broadcast%20equipment%2C%20cinematic%20wide%20shot%20with%20shallow%20depth%20of%20field%2C%20modern%20radio%20station%20control%20room%20atmosphere&width=1600&height=900&seq=radios-hero-banner&orientation=landscape"
          alt="Directorio Global de Radios WEB"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
            <i className="ri-broadcast-line text-3xl text-background-50"></i>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Directorio Global de Radios
          </h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
            El directorio maestro de RadiofrecuenciaRFW. Descubre, escucha y conecta con las mejores radios RFW en español.
          </p>
          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center gap-2 text-background-50/90 text-xs">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
              <span className="font-bold">{liveCount} en vivo ahora</span>
            </div>
            <div className="w-px h-4 bg-background-50/20"></div>
            <span className="text-background-50/70 text-xs">{globalRadioStations.length} emisoras</span>
            <div className="w-px h-4 bg-background-50/20"></div>
            <span className="text-background-50/70 text-xs">{totalListeners.toLocaleString()} oyentes</span>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-background-100 py-5">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex flex-wrap gap-5 items-center justify-center">
          {[
            { icon: 'ri-radio-line', label: 'Emisoras', value: globalRadioStations.length.toString() },
            { icon: 'ri-headphone-line', label: 'Oyentes Totales', value: totalListeners.toLocaleString() },
            { icon: 'ri-map-pin-line', label: 'Ciudades', value: allCities.length.toString() },
            { icon: 'ri-global-line', label: 'Países', value: allCountries.length.toString() },
            { icon: 'ri-price-tag-3-line', label: 'Categorías', value: allCategories.length.toString() },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 px-4">
              <div className="w-10 h-10 rounded-xl bg-background-200 flex items-center justify-center">
                <i className={`${stat.icon} text-base text-foreground-500`}></i>
              </div>
              <div>
                <p className="text-lg font-extrabold text-foreground-950 leading-none">{stat.value}</p>
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8">
        {/* Filters + Sort + View */}
        <section>
          <FilterBar
            search={search}
            onSearchChange={setSearch}
            category={category}
            onCategoryChange={setCategory}
            city={city}
            onCityChange={setCity}
            country={country}
            onCountryChange={setCountry}
            language={language}
            onLanguageChange={setLanguage}
            isLive={isLive}
            onIsLiveChange={setIsLive}
            radioScoreMin={radioScoreMin}
            onRadioScoreMinChange={setRadioScoreMin}
            sort={sort}
            onSortChange={setSort}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            categories={allCategories}
            cities={allCities}
            countries={allCountries}
            languages={allLanguages}
            onClearAll={clearAllFilters}
          />
        </section>

        {/* MAP VIEW PLACEHOLDER */}
        {viewMode === 'map' && (
          <section className="bg-background-100 rounded-3xl p-8 md:p-12 flex flex-col items-center justify-center text-center min-h-[400px] border border-background-200 border-dashed">
            <div className="w-20 h-20 rounded-2xl bg-background-200 flex items-center justify-center mb-5">
              <i className="ri-map-pin-line text-3xl text-foreground-300"></i>
            </div>
            <h3 className="text-lg font-extrabold text-foreground-500 mb-2">Vista de Mapa</h3>
            <p className="text-sm text-foreground-400 max-w-md leading-relaxed">
              La vista de mapa estará disponible próximamente. Podrás ver todas las emisoras distribuidas geográficamente y filtrar por región.
            </p>
            <div className="flex items-center gap-2 mt-5">
              <button
                onClick={() => setViewMode('grid')}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-background-50 border border-background-200 text-foreground-700 rounded-xl text-xs font-bold hover:border-foreground-300 cursor-pointer transition-all whitespace-nowrap"
              >
                <i className="ri-layout-grid-line"></i>
                Ver en Grid
              </button>
              <button
                onClick={() => setViewMode('list')}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-background-50 border border-background-200 text-foreground-700 rounded-xl text-xs font-bold hover:border-foreground-300 cursor-pointer transition-all whitespace-nowrap"
              >
                <i className="ri-list-check"></i>
                Ver en Lista
              </button>
            </div>
          </section>
        )}

        {/* RESULTS */}
        {viewMode !== 'map' && (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold text-foreground-500">
                  {filtered.length} radio{filtered.length !== 1 ? 's' : ''} encontrada{filtered.length !== 1 ? 's' : ''}
                </p>
                {isLive === true && (
                  <span className="px-2 py-0.5 rounded-full bg-red-500 text-[9px] font-bold text-background-50">En vivo</span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <Link
                  to="/explorar"
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-compass-3-line text-xs"></i>
                  Descubrir más
                </Link>
                <Link
                  to="/events"
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-foreground-400 hover:text-foreground-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-calendar-event-line text-xs"></i>
                  Ver eventos globales
                </Link>
              </div>
            </div>

            {filtered.length > 0 ? (
              viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filtered.map((radio) => (
                    <RadioCard key={radio.id} radio={radio} viewMode="grid" />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {filtered.map((radio) => (
                    <RadioCard key={radio.id} radio={radio} viewMode="list" />
                  ))}
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-search-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-sm font-semibold text-foreground-400">No encontramos radios con esos filtros.</p>
                <p className="text-[12px] text-foreground-300 mt-1 mb-6">Probá ajustando los filtros o descubrí todas las radios.</p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      clearAllFilters();
                      SwalAlert.toast('Filtros limpiados', 'info');
                    }}
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-foreground-600 text-xs font-bold hover:border-foreground-300 hover:text-foreground-800 cursor-pointer transition-all whitespace-nowrap"
                  >
                    <i className="ri-filter-off-line text-xs"></i>
                    Limpiar filtros
                  </button>
                  <Link
                    to="/explorar"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-accent-500 text-background-50 text-xs font-bold hover:bg-accent-600 cursor-pointer transition-all whitespace-nowrap"
                  >
                    <i className="ri-compass-3-line text-xs"></i>
                    Descubrir radios
                  </Link>
                </div>
              </div>
            )}
          </>
        )}

        {/* TOP FEATURED SECTION */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center">
              <i className="ri-star-fill text-base text-accent-600"></i>
            </div>
            <h2 className="text-xl font-extrabold text-foreground-950">Radios Destacadas</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {globalRadioStations
              .filter((r) => r.featured)
              .map((radio) => (
                <RadioCard key={radio.id} radio={radio} viewMode="grid" />
              ))}
          </div>
        </section>
      </main>
    </div>
  );
}